import React, { useMemo, useRef, useState } from 'react'
import { COLORS, IMAGE_COLORS, createProjector, toSvgPoints, scaleBarMetres } from './sprayReportUtils'

const MAP_W = 600
const MAP_H = 400

let clipIdCounter = 0

const validPolygon = (vertices) => Array.isArray(vertices) && vertices.length > 2

// Telemetry further than this outside the field's bounding box is treated as transit, not spraying
const TELEMETRY_MARGIN_M = 15

// Spray segments from the report; falls back to the parts of the flight's telemetry trail that lie over the field
export const getSprayPaths = (report, telemetry) => {
    const segments = (report?.flight_path?.spray_segments || []).filter(s => Array.isArray(s) && s.length > 1)
    if (segments.length) return segments

    const area = [report?.field_dimensions?.field_outline?.vertices_lat_lon, report?.field_dimensions?.target_spray_area?.vertices_lat_lon]
        .filter(validPolygon).flat()
    const trail = (telemetry || []).filter(p => p?.lat && p?.lng).map(p => [Number(p.lat), Number(p.lng)])
    if (!area.length) return trail.length > 1 ? [trail] : []

    const lats = area.map(p => p[0]), lons = area.map(p => p[1])
    const dLat = TELEMETRY_MARGIN_M / 111320
    const dLon = dLat / Math.cos(lats[0] * Math.PI / 180)
    const inside = ([lat, lon]) => lat >= Math.min(...lats) - dLat && lat <= Math.max(...lats) + dLat
        && lon >= Math.min(...lons) - dLon && lon <= Math.max(...lons) + dLon

    const result = []
    let current = []
    for (const p of trail) {
        if (inside(p)) current.push(p)
        else if (current.length) { result.push(current); current = [] }
    }
    if (current.length) result.push(current)
    return result.filter(s => s.length > 1)
}

const useMapGeometry = (report, paths) => useMemo(() => {
    const field = report?.field_dimensions?.field_outline?.vertices_lat_lon
    const target = report?.field_dimensions?.target_spray_area?.vertices_lat_lon
    const all = [
        ...(validPolygon(field) ? field : []),
        ...(validPolygon(target) ? target : []),
        ...paths.flat(),
    ]
    if (!all.length) return null
    const projector = createProjector(all, MAP_W, MAP_H)
    return {
        ...projector,
        field: validPolygon(field) ? toSvgPoints(field, projector.project) : null,
        target: validPolygon(target) ? toSvgPoints(target, projector.project) : null,
        paths: paths.map(p => toSvgPoints(p, projector.project)),
        arrows: paths.flatMap(p => directionArrows(p, projector.project)),
    }
}, [report, paths])

// One arrow in the middle of every leg that is long enough to show it
const directionArrows = (latLngs, project) => {
    const arrows = []
    for (let i = 1; i < latLngs.length; i++) {
        const [x1, y1] = project(latLngs[i - 1])
        const [x2, y2] = project(latLngs[i])
        if (Math.hypot(x2 - x1, y2 - y1) < 40) continue
        arrows.push({ x: (x1 + x2) / 2, y: (y1 + y2) / 2, angle: Math.atan2(y2 - y1, x2 - x1) * 180 / Math.PI })
    }
    return arrows
}

const MAP_INK = '#4B5563'

const ScaleBar = ({ pxPerMetre }) => {
    const metres = scaleBarMetres(pxPerMetre, MAP_W)
    const len = metres * pxPerMetre
    return (
        <g transform={`translate(0, ${MAP_H - 16})`}>
            <text x="4" y="-5" fontSize="13" fill={MAP_INK} fontFamily="DM Mono, monospace">{metres} m</text>
            <line x1="0" y1="0" x2={len} y2="0" stroke={MAP_INK} strokeWidth="1.5" />
        </g>
    )
}

const DashedPaths = ({ paths }) => paths.map((p, i) => (
    <polyline key={i} points={p} fill="none" stroke={COLORS.path} strokeWidth="1.5" strokeDasharray="6 4" strokeLinejoin="round" />
))

const Arrows = ({ arrows, color }) => arrows.map((a, i) => (
    <path key={i} d="M-4 -3.5 L4 0 L-4 3.5 Z" fill={color} transform={`translate(${a.x.toFixed(1)} ${a.y.toFixed(1)}) rotate(${a.angle.toFixed(1)})`} />
))

const EmptyMap = ({ text }) => <div className="sqr-map sqr-map-empty"><span>{text}</span></div>

// The report's own map image, if it sends one; when it is missing or fails to load, the map is drawn from the report data
export const useMapImage = (url) => {
    const [failed, setFailed] = useState([])
    const src = url && !failed.includes(url) ? url : null
    return { url: src, onError: () => setFailed(f => [...f, src]) }
}

const MapImage = ({ image, alt }) => (
    <div className="sqr-map-photo"><img alt={alt} src={image.url} onError={image.onError} /></div>
)

export const FlightRouteMap = ({ report, paths, image }) => {
    const geo = useMapGeometry(report, paths)
    if (image?.url) return <MapImage image={image} alt="Flight route" />
    if (!geo) return <EmptyMap text="No route data available" />
    return (
        <div className="sqr-map sqr-map-plain">
            <svg className="sqr-map-layer" viewBox={`0 0 ${MAP_W} ${MAP_H}`} preserveAspectRatio="xMidYMid meet">
                {geo.field && <polygon points={geo.field} fill={COLORS.fieldFill} stroke={COLORS.field} strokeWidth="2.5" strokeLinejoin="round" />}
                {geo.target && <polygon points={geo.target} fill={COLORS.fieldFill} stroke={COLORS.sprayArea} strokeWidth="2.5" strokeLinejoin="round" />}
                <DashedPaths paths={geo.paths} />
                <ScaleBar pxPerMetre={geo.pxPerMetre} />
            </svg>
        </div>
    )
}

export const CoverageMap = ({ report, paths, image }) => {
    const geo = useMapGeometry(report, paths)
    const clipId = useRef(`sqr-clip-${++clipIdCounter}`).current
    const sprayWidth = Number(report?.analysis_parameters?.spray_width_m) || 0

    // A ready-made coverage image from the analysis service shows exact overlap counts, so prefer it
    if (image?.url) return <MapImage image={image} alt="Coverage map" />
    if (!geo || !geo.target) return <EmptyMap text="No coverage data available" />

    if (!geo.paths.length) {
        return (
            <div className="sqr-map sqr-map-plain">
                <svg className="sqr-map-layer" viewBox={`0 0 ${MAP_W} ${MAP_H}`} preserveAspectRatio="xMidYMid meet">
                    {geo.field && <polygon points={geo.field} fill="#FFFFFF" fillOpacity="0.3" stroke={COLORS.fieldOutline} strokeWidth="1.2" strokeDasharray="5 4" />}
                    <polygon points={geo.target} fill="none" stroke={COLORS.target} strokeWidth="1.5" strokeDasharray="6 4" />
                    <text x={MAP_W / 2} y={MAP_H - 14} textAnchor="middle" fontSize="14" fill="#4B5563">Cell-by-cell coverage is not available for this report</text>
                    <ScaleBar pxPerMetre={geo.pxPerMetre} />
                </svg>
            </div>
        )
    }

    const swathPx = Math.max(sprayWidth * geo.pxPerMetre, 1)
    return (
        <div className="sqr-map sqr-map-plain">
            <svg className="sqr-map-layer" viewBox={`0 0 ${MAP_W} ${MAP_H}`} preserveAspectRatio="xMidYMid meet">
                <defs>
                    <clipPath id={clipId}><polygon points={geo.target} /></clipPath>
                </defs>
                {geo.field && <polygon points={geo.field} fill="none" stroke={COLORS.fieldOutline} strokeWidth="1.2" strokeDasharray="5 4" />}
                {/* Swath drawn unclipped first, so only the part outside the target stays visible (purple) */}
                {geo.paths.map((p, i) => <polyline key={`o${i}`} points={p} fill="none" stroke={COLORS.offTarget} strokeWidth={swathPx} strokeLinecap="round" strokeLinejoin="round" />)}
                <polygon points={geo.target} fill={COLORS.missed} />
                <g clipPath={`url(#${clipId})`}>
                    {geo.paths.map((p, i) => <polyline key={`c${i}`} points={p} fill="none" stroke={COLORS.coveredOnce} strokeWidth={swathPx} strokeLinecap="round" strokeLinejoin="round" />)}
                </g>
                <polygon points={geo.target} fill="none" stroke={COLORS.target} strokeWidth="1.5" strokeDasharray="6 4" />
                <DashedPaths paths={geo.paths} />
                <Arrows arrows={geo.arrows} color={COLORS.path} />
                <ScaleBar pxPerMetre={geo.pxPerMetre} />
            </svg>
        </div>
    )
}

export const routeLegend = (fromImage) => fromImage ? [
    { label: 'Field boundary', color: IMAGE_COLORS.routeField, type: 'line' },
    { label: 'Spray area', color: IMAGE_COLORS.routeSprayArea, type: 'line' },
    { label: 'Flight path', color: IMAGE_COLORS.routePath, type: 'halo' },
] : [
    { label: 'Field boundary', color: COLORS.field, type: 'line' },
    { label: 'Spray area', color: COLORS.sprayArea, type: 'line' },
    { label: 'Flight path', color: COLORS.path, type: 'dash' },
]

// Two columns, read left to right: the swatches first, then the outlines
export const coverageLegend = (fromImage) => fromImage ? [
    { label: 'Covered once', color: IMAGE_COLORS.coveredOnce, type: 'box' },
    { label: 'Overlap (2 passes)', color: IMAGE_COLORS.overlap2x, type: 'box' },
    { label: 'Overlap (3+ passes)', color: IMAGE_COLORS.overlap3x, type: 'box' },
    { label: 'Missed (not sprayed)', color: IMAGE_COLORS.missed, type: 'box' },
    { label: 'Target spray boundary', color: IMAGE_COLORS.target, type: 'line' },
    { label: 'Field outline', color: IMAGE_COLORS.fieldOutline, type: 'dash' },
    { label: 'Drone path (spraying)', color: IMAGE_COLORS.path, type: 'line' },
    { label: 'Start / end', color: IMAGE_COLORS.coveredOnce, type: 'start-end' },
] : [
    { label: 'Covered once', color: COLORS.coveredOnce, type: 'box' },
    { label: 'Overlap (2 passes)', color: COLORS.overlap2x, type: 'box' },
    { label: 'Overlap (3+ passes)', color: COLORS.overlap3x, type: 'box' },
    { label: 'Missed (not sprayed)', color: COLORS.missed, type: 'box' },
    { label: 'Spray outside target', color: COLORS.offTarget, type: 'box' },
    { label: 'Target spray boundary', color: COLORS.target, type: 'box' },
    { label: 'Field outline', color: COLORS.fieldOutline, type: 'dash' },
    { label: 'Drone path (spraying)', color: COLORS.path, type: 'dash' },
]
